import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { Task } from '@cleartask/shared';
import { useNavItems } from '../../navigation.js';
import type { TaskListResponse } from '../../lib/api.js';
import { taskKeys, useCompleteTask, useReopenTask, useSnoozeTask } from '../tasks/useTasks.js';
import { CommandPalette } from './CommandPalette.js';
import { ShortcutsHelp } from './ShortcutsHelp.js';

/**
 * O evento veio de um campo de texto?
 *
 * Sem esta checagem, digitar "n" numa nova tarefa dispararia o atalho de nova
 * tarefa — atalhos de tecla unica sao inutilizaveis num app cheio de campos de
 * texto se nao respeitarem o foco.
 */
function digitandoEm(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false;
  if (alvo.isContentEditable) return true;

  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo.tagName);
}

/**
 * Atalhos de teclado (spec 10.2) e paleta de comandos.
 *
 * `N` nova tarefa · `/` busca · `1..5` trocar de aba · `E` concluir ·
 * `S` adiar · `⌘K` paleta · `Esc` fechar.
 *
 * `E` e `S` agem sobre o card que CONTEM o foco, encontrado pelo `data-task-id`
 * do `<li>`. A alternativa seria um estado de "card selecionado" paralelo ao
 * foco do navegador — dois cursores na mesma tela, e o invisivel seria
 * justamente o que o leitor de tela nao anuncia.
 */
export function GlobalShortcuts() {
  const [ajuda, setAjuda] = useState(false);
  const [paleta, setPaleta] = useState<{ open: boolean; query: string }>({
    open: false,
    query: '',
  });

  const navigate = useNavigate();
  // As teclas seguem a barra: `3` e a terceira aba que a pessoa VE.
  const abas = useNavItems();
  const qc = useQueryClient();
  const completeTask = useCompleteTask();
  const reopenTask = useReopenTask();
  const snoozeTask = useSnoozeTask();

  const fecharPaleta = useCallback(() => setPaleta({ open: false, query: '' }), []);

  /**
   * A tarefa do card em foco.
   *
   * Vem do cache do TanStack Query, e nao de uma nova requisicao: o card so
   * esta na tela porque a consulta que o trouxe ja esta em cache, e buscar de
   * novo tornaria um atalho de teclado dependente de rede.
   */
  const tarefaEmFoco = useCallback((): Task | null => {
    const ativo = document.activeElement;
    if (!(ativo instanceof HTMLElement)) return null;

    const card = ativo.closest<HTMLElement>('[data-task-id]');
    const id = card?.dataset['taskId'];
    if (!id) return null;

    for (const [, valor] of qc.getQueriesData<TaskListResponse>({ queryKey: taskKeys.all })) {
      const encontrada = valor?.tasks.find((t) => t.id === id);
      if (encontrada) return encontrada;
    }
    return null;
  }, [qc]);

  useEffect(() => {
    function aoTeclar(event: KeyboardEvent) {
      const comModificador = event.metaKey || event.ctrlKey;

      // ⌘K / Ctrl+K funciona mesmo dentro de um campo: e o unico atalho que a
      // spec preve com modificador, e e a saida de emergencia de qualquer tela.
      if (comModificador && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaleta({ open: true, query: '' });
        return;
      }

      // Esc fecha o que estiver aberto e, nao havendo nada, devolve o foco ao
      // documento — util para sair de um campo sem usar o mouse.
      if (event.key === 'Escape') {
        if (ajuda) {
          setAjuda(false);
        } else if (paleta.open) {
          setPaleta({ open: false, query: '' });
        } else if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        return;
      }

      if (comModificador || event.altKey) return;
      if (digitandoEm(event.target)) return;
      if (paleta.open) return;

      // `?` exige Shift na maioria dos layouts, entao e lido de `event.key`
      // direto, antes do `toLowerCase()` que os atalhos de letra usam.
      if (event.key === '?') {
        event.preventDefault();
        setAjuda((atual) => !atual);
        return;
      }

      if (ajuda) return;

      const tecla = event.key.toLowerCase();

      if (tecla === 'n') {
        event.preventDefault();
        const campo = document.querySelector<HTMLInputElement>('[data-quick-add]');
        // A entrada rapida so existe na tela de tarefas; ir para la primeiro e
        // o que faz o atalho valer em qualquer aba.
        if (campo) campo.focus();
        else navigate('/tarefas');
        return;
      }

      if (tecla === '/') {
        event.preventDefault();
        setPaleta({ open: true, query: '' });
        return;
      }

      if (tecla >= '1' && tecla <= '5') {
        const item = abas[Number(tecla) - 1];
        if (item) {
          event.preventDefault();
          navigate(item.path);
        }
        return;
      }

      // `E` alterna, como o checkbox do card. Chamar `complete` numa tarefa ja
      // concluida nao seria inofensivo: a rota gera a proxima ocorrencia de uma
      // serie que ja avancou, entao segurar a tecla duplicaria a agenda.
      if (tecla === 'e') {
        const task = tarefaEmFoco();
        if (!task) return;
        event.preventDefault();
        if (task.status === 'done') reopenTask.mutate(task);
        else completeTask.mutate(task);
        return;
      }

      if (tecla === 's') {
        const task = tarefaEmFoco();
        if (!task) return;
        // O card concluido nao mostra "Adiar" (os presets sao sempre futuros, e
        // adiar o que ja foi feito nao descreve intencao nenhuma). O atalho
        // segue a tela: sem `preventDefault`, para a tecla continuar livre.
        if (task.status === 'done') return;
        event.preventDefault();
        // "Amanha as 09:00" e o preset mais usado da spec 4.3; os outros
        // continuam no menu, que o atalho nao substitui.
        snoozeTask.mutate({ task, payload: { preset: 'tomorrow' } });
      }
    }

    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [navigate, abas, paleta.open, ajuda, tarefaEmFoco, completeTask, reopenTask, snoozeTask]);

  return (
    <>
      <CommandPalette open={paleta.open} initialQuery={paleta.query} onClose={fecharPaleta} />
      <ShortcutsHelp open={ajuda} onClose={() => setAjuda(false)} />
    </>
  );
}
